/**
 * WeeklyTasksTab — FSR/UB-facing weekly work queue.
 *
 * Renders the calling officer's weekly tasks grouped by category in
 * collapsible accordion sections. Replaces the legacy SP-hub
 * "FSR/UB Weekly Report" Excel process (Bill 2026-04-25).
 *
 * Empty state when no tasks → friendly copy ("No weekly tasks this week —
 * nice!"). Per-category empty state when filters narrow to nothing.
 *
 * UX rules:
 *   - NO GUIDs, NO Cart IDs, NO raw codes shown to user — humanize everything
 *   - AccountNumber is masked-end (last 4) by default; click to reveal full
 *   - Filter row: status chips + category chips
 *   - Per-card actions: Mark In Progress / Notes / Mark Complete
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Banner, FilterChips, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import type {
  WeeklyTask,
  WeeklyTaskCategory,
  WeeklyTaskStatus,
} from '@retail-ops/shared-types';

const CATEGORY_LABEL: Record<WeeklyTaskCategory, string> = {
  NEWESI: 'New accounts — ESI enrollment follow-up',
  NOFUND: 'New accounts — not yet funded',
  NEWOD: 'New DDA — overdraft protection within next week',
  NEWFOL: 'New accounts — past week (parent listing)',
  ZROCLS: 'Zero-balance DDA — 3+ weeks (auto-close 30d)',
};

const CATEGORY_PILLAR: Record<WeeklyTaskCategory, string> = {
  NEWESI: 'Onboarding',
  NOFUND: 'Onboarding',
  NEWOD: 'Servicing',
  NEWFOL: 'Onboarding context',
  ZROCLS: 'Nurturing',
};

const STATUS_OPTIONS: ReadonlyArray<{ value: WeeklyTaskStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
];

const CATEGORY_OPTIONS: ReadonlyArray<{ value: WeeklyTaskCategory | 'all'; label: string }> = [
  { value: 'all', label: 'All categories' },
  { value: 'NEWESI', label: 'ESI enrollment' },
  { value: 'NOFUND', label: 'Not funded' },
  { value: 'NEWOD', label: 'New OD' },
  { value: 'NEWFOL', label: 'New (parent)' },
  { value: 'ZROCLS', label: 'Zero-balance close' },
];

function maskAccountEnd(account: string): string {
  if (!account) return '';
  if (account.length <= 4) return account;
  return `••••${account.slice(-4)}`;
}

function ageBadgeTone(ageDays: number): 'muted' | 'warn' | 'err' {
  if (ageDays <= 7) return 'muted';
  if (ageDays <= 14) return 'warn';
  return 'err';
}

export function WeeklyTasksTab(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();

  const officerCode = user?.officerCode ?? '';

  const [tasks, setTasks] = useState<ReadonlyArray<WeeklyTask>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<WeeklyTaskStatus | 'all'>('open');
  const [categoryFilter, setCategoryFilter] = useState<WeeklyTaskCategory | 'all'>('all');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  const refresh = useCallback(async (): Promise<void> => {
    if (!officerCode) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const filters: { officer: string; status?: WeeklyTaskStatus; category?: WeeklyTaskCategory } = {
        officer: officerCode,
      };
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (categoryFilter !== 'all') filters.category = categoryFilter;
      const resp = await client.retail.tasks.weekly.list(filters);
      setTasks(resp.tasks ?? []);
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : 'Failed to load weekly tasks.';
      setError(msg);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [client, officerCode, statusFilter, categoryFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const grouped = useMemo<Record<WeeklyTaskCategory, ReadonlyArray<WeeklyTask>>>(() => {
    const out: Record<string, WeeklyTask[]> = {
      NEWESI: [],
      NOFUND: [],
      NEWOD: [],
      NEWFOL: [],
      ZROCLS: [],
    };
    for (const t of tasks) {
      if (out[t.taskCategory]) out[t.taskCategory].push(t);
    }
    return out as Record<WeeklyTaskCategory, ReadonlyArray<WeeklyTask>>;
  }, [tasks]);

  const totalShown = tasks.length;

  if (!officerCode) {
    return (
      <Banner tone="warn">
        Your profile has no Officer Code on file — weekly tasks are assigned by
        officer code. Contact your supervisor to set one.
      </Banner>
    );
  }

  return (
    <div>
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="err">{error}</Banner>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 16,
          padding: '12px 16px',
          background: 'var(--surface-subtle)',
          border: '1px solid var(--surface-border)',
          borderRadius: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 4 }}>Status</div>
          <FilterChips
            options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            selected={statusFilter}
            onChange={(v) => setStatusFilter(v as WeeklyTaskStatus | 'all')}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 4 }}>Category</div>
          <FilterChips
            options={CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            selected={categoryFilter}
            onChange={(v) => setCategoryFilter(v as WeeklyTaskCategory | 'all')}
          />
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-600)' }}>
          {loading ? 'Loading…' : `${totalShown} task${totalShown === 1 ? '' : 's'}`}
        </div>
      </div>

      {!loading && totalShown === 0 && (
        <div
          style={{
            padding: '32px 24px',
            textAlign: 'center',
            background: 'var(--surface-card)',
            border: '1px dashed var(--surface-border)',
            borderRadius: 8,
            color: 'var(--ink-600)',
          }}
        >
          <div style={{ fontSize: 18, color: 'var(--pcb-green)', marginBottom: 6 }}>
            No weekly tasks this week — nice!
          </div>
          <div>You're caught up across every category.</div>
        </div>
      )}

      {(Object.keys(grouped) as WeeklyTaskCategory[]).map((cat) => {
        const rows = grouped[cat];
        if (rows.length === 0) return null;
        const isCollapsed = collapsed[cat] === true;
        return (
          <section
            key={cat}
            style={{
              marginBottom: 16,
              background: 'var(--surface-card)',
              border: '1px solid var(--surface-border)',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <button
              type="button"
              onClick={() => setCollapsed((prev) => ({ ...prev, [cat]: !isCollapsed }))}
              style={{
                width: '100%',
                display: 'flex',
                gap: 12,
                alignItems: 'center',
                padding: '12px 16px',
                background: 'var(--surface-subtle)',
                border: 0,
                borderBottom: isCollapsed ? 0 : '1px solid var(--surface-border)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 14, color: 'var(--pcb-blue-dark)', fontWeight: 600 }}>
                {CATEGORY_LABEL[cat]}
              </span>
              <Pill variant="muted">{CATEGORY_PILLAR[cat]}</Pill>
              <span
                style={{
                  marginLeft: 'auto',
                  fontSize: 12,
                  color: 'var(--ink-600)',
                  fontWeight: 500,
                }}
              >
                {rows.length} {rows.length === 1 ? 'task' : 'tasks'}
                {' '}·{' '}{isCollapsed ? 'expand' : 'collapse'}
              </span>
            </button>

            {!isCollapsed && (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {rows.map((t) => (
                  <WeeklyTaskCard
                    key={t.id}
                    task={t}
                    revealed={revealed[t.id] === true}
                    onReveal={() => setRevealed((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
                    onChanged={refresh}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// WeeklyTaskCard
// ─────────────────────────────────────────────────────────────────────────

interface CardProps {
  task: WeeklyTask;
  revealed: boolean;
  onReveal: () => void;
  onChanged: () => Promise<void> | void;
}

function WeeklyTaskCard({ task, revealed, onReveal, onChanged }: CardProps): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const [notes, setNotes] = useState<string>(task.notes ?? '');
  const [reason, setReason] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const ageTone = ageBadgeTone(task.ageDays);
  const isTerminal = task.status === 'completed' || task.status === 'expired'
    || task.status === 'account-auto-closed';

  const saveNotes = useCallback(async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await client.retail.tasks.weekly.update(task.id, {
        notes,
        updatedBy: user?.upn ?? user?.officerCode ?? 'unknown',
      });
      await onChanged();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }, [client, task.id, notes, user, onChanged]);

  const markInProgress = useCallback(async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await client.retail.tasks.weekly.update(task.id, {
        status: 'in-progress',
        updatedBy: user?.upn ?? user?.officerCode ?? 'unknown',
      });
      await onChanged();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Update failed.');
    } finally {
      setBusy(false);
    }
  }, [client, task.id, user, onChanged]);

  const complete = useCallback(async (): Promise<void> => {
    if (reason.trim().length < 5) {
      setErr('Completion reason must be at least 5 characters.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await client.retail.tasks.weekly.complete(task.id, {
        completionReason: reason.trim(),
        completedBy: user?.upn ?? user?.officerCode ?? 'unknown',
      });
      await onChanged();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Complete failed.');
    } finally {
      setBusy(false);
    }
  }, [client, task.id, reason, user, onChanged]);

  return (
    <article style={{ padding: '14px 16px', borderTop: '1px solid var(--surface-border)' }}>
      <header style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
          {task.customerName ?? '(unnamed customer)'}
        </span>
        <button
          type="button"
          onClick={onReveal}
          style={{
            border: '1px solid var(--surface-border)',
            background: 'var(--surface-card)',
            borderRadius: 4,
            fontSize: 12,
            padding: '2px 8px',
            color: 'var(--ink-600)',
            cursor: 'pointer',
            fontFamily: 'monospace',
          }}
          aria-label="Toggle account number reveal"
        >
          {revealed ? task.accountNumber : maskAccountEnd(task.accountNumber)}
        </button>
        <Pill variant="muted">{task.accountType}</Pill>
        <Pill variant="muted">Branch {task.branch}</Pill>
        <Pill variant={ageTone}>
          {task.ageDays}d old
        </Pill>
        <Pill
          variant={
            task.status === 'completed' ? 'ok'
              : task.status === 'expired' ? 'err'
              : task.status === 'in-progress' ? 'info'
              : 'muted'
          }
        >
          {task.status}
        </Pill>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-500)' }}>
          Week {task.weekStartDate}
        </span>
      </header>

      {err && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--pcb-red)' }}>
          {err}
        </div>
      )}

      {!isTerminal && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes — e.g. left voicemail, sent secure message…"
            disabled={busy}
            rows={2}
            style={{
              width: '100%',
              fontFamily: 'inherit',
              fontSize: 13,
              padding: 8,
              border: '1px solid var(--surface-border)',
              borderRadius: 4,
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => void saveNotes()}
              disabled={busy || notes === (task.notes ?? '')}
              style={btnStyle('secondary')}
            >
              Save notes
            </button>
            {task.status === 'open' && (
              <button
                type="button"
                onClick={() => void markInProgress()}
                disabled={busy}
                style={btnStyle('secondary')}
              >
                Mark In Progress
              </button>
            )}
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason — required to complete (min 5 chars)"
              disabled={busy}
              style={{
                flex: 1,
                minWidth: 220,
                fontSize: 13,
                padding: '6px 8px',
                border: '1px solid var(--surface-border)',
                borderRadius: 4,
              }}
            />
            <button
              type="button"
              onClick={() => void complete()}
              disabled={busy || reason.trim().length < 5}
              style={btnStyle('primary')}
            >
              Mark Complete
            </button>
          </div>
        </div>
      )}

      {isTerminal && (
        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--ink-600)' }}>
          {task.status === 'completed' && task.completedAt && (
            <>
              Completed {new Date(task.completedAt).toLocaleDateString()} by{' '}
              <strong>{task.completedBy ?? 'unknown'}</strong>
              {task.completionReason && (
                <span> — &ldquo;{task.completionReason}&rdquo;</span>
              )}
            </>
          )}
          {task.status === 'expired' && <span>Task expired (week rolled over).</span>}
          {task.status === 'account-auto-closed' && (
            <span>Account was auto-closed by Core Director.</span>
          )}
        </div>
      )}
    </article>
  );
}

function btnStyle(variant: 'primary' | 'secondary'): React.CSSProperties {
  if (variant === 'primary') {
    return {
      padding: '6px 14px',
      fontSize: 13,
      background: 'var(--brand-fill)',
      color: 'var(--on-brand)',
      border: '1px solid var(--pcb-blue)',
      borderRadius: 4,
      cursor: 'pointer',
      fontWeight: 500,
    };
  }
  return {
    padding: '6px 14px',
    fontSize: 13,
    background: 'var(--surface-card)',
    color: 'var(--pcb-blue-dark)',
    border: '1px solid var(--surface-border)',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 500,
  };
}
